import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {EventModelUpsertType} from "../../../_typing/model-uploads";
import {BehaviorSubject, combineLatest, ReplaySubject, Subject} from "rxjs";
import {Papa} from "ngx-papaparse";
import {takeUntil} from "rxjs/operators";
import {EventModelUploadDataService} from "./event-model-upload-data.service";
import {ConstantProperty} from "../../../_models/constant-property";
import {UploadModelTemplateService} from "../upload-model-template.service";
import {NotificationService} from "../../../services/notification.service";
import {COMMON_FORM_IMPORTS} from "../../../shared/import-groups/Common";
import {
    EventModelUploadSelectEventTypeComponent
} from "./event-model-upload-select-event-type/event-model-upload-select-event-type.component";
import {
    EventModelUploadSelectUniquePropertyComponent
} from "./event-model-upload-select-unique-property/event-model-upload-select-unique-property.component";
import {
    EventModelUploadSelectComponentTypeComponent
} from "./event-model-upload-select-component-type/event-model-upload-select-component-type.component";
import {
    EventModelUploadSelectConstantPropertyComponent
} from "./event-model-upload-select-constant-property/event-model-upload-select-constant-property.component";
import {EventModelOptions, HeaderAssociation, HeaderType} from "../../../_typing/model-uploads";



@Component({
    selector: 'extract-event-options',
    templateUrl: './extract-event-options.component.html',
    styleUrls: ['./extract-event-options.component.less'],
    imports: [
    COMMON_FORM_IMPORTS,
    EventModelUploadSelectEventTypeComponent,
    EventModelUploadSelectUniquePropertyComponent,
    EventModelUploadSelectComponentTypeComponent,
    EventModelUploadSelectConstantPropertyComponent
],
    providers: [EventModelUploadDataService]
})
export class ExtractEventOptionsComponent implements OnInit, OnDestroy {
    private readonly onDestroy = new Subject<void>();

    // instantiate this constant class rather make properties static to make it work in the template.
    public readonly HEADER_TYPES = new HeaderType();
    readonly REQUIRED_HEADERS: string[] = [
        this.HEADER_TYPES.START_DATE,
    ];

    // headers that may at most have one association
    readonly SINGLE_HEADERS: string[] = [
        this.HEADER_TYPES.START_DATE,
        this.HEADER_TYPES.END_DATE,
    ];
    // headers that can have multiple column associations
    readonly MULTI_HEADERS = [
        this.HEADER_TYPES.UNUSED,
        this.HEADER_TYPES.PROPERTY,
        this.HEADER_TYPES.COMPONENT,
    ];
    readonly DESCRIPTIONS: { [key: string]: string } = HeaderType.descriptions();

    upsert_types: { name: string, value: EventModelUpsertType, disabled?: boolean }[] = [
        {name: 'Unique Property', value: "unique_property"},
    ];

    requires_id = [
        this.HEADER_TYPES.PROPERTY,
        this.HEADER_TYPES.COMPONENT,
    ];

    model_upload_features: string[] = [];

    available_properties: ConstantProperty[] = [];

    header_associations: HeaderAssociation[] = [];
    had_file: boolean = null;
    // List of available Constant Properties. Used for add overrides
    constant_properties: { id: string, name: string }[] = [];
    // actions still required to make configuration valid
    required_configurations: string[] = [];
    error: string = '';
    // subject containing data from the current file
    fileDataSubject: Subject<string[][]> = new ReplaySubject(1);
    // If a template is user to populate the local options, this subject will emit the template options
    templateOptionsSubject: Subject<EventModelOptions> = new ReplaySubject(1);
    // Emits when changes are made to the local options
    localOptionsSubject: BehaviorSubject<EventModelOptions>;
    // If either the template or local options has changed
    finalOptionsSubject: Subject<EventModelOptions> = new Subject<EventModelOptions>();
    displayOptions: EventModelOptions;
    component_types: { id: string, name: string }[] = [];

    @Output()
    preview: EventEmitter<any> = new EventEmitter<any>();

    constructor(private papa: Papa,
                private templateService: UploadModelTemplateService,
                public dataService: EventModelUploadDataService,
                private notification: NotificationService) {
        this.model_upload_features = this.MULTI_HEADERS.concat(this.SINGLE_HEADERS);
        this.localOptionsSubject = new BehaviorSubject<EventModelOptions>(new EventModelOptions());
        this.displayOptions = this.localOptionsSubject.getValue();
        this.templateService.templateChanged$.pipe(takeUntil(this.onDestroy)).subscribe(options => {
            this.templateOptionsSubject.next(options.attributes.json as EventModelOptions);
        });
    }

    @Input() readonly = false;

    @Input()
    set file(file) {
        if (!file) {
            if (this.had_file) {
                this.fileDataSubject.next(file);
            }
            this.had_file = false;
            return;
        }

        this.papa.parse(file, {
            worker: true,
            preview: 50,
            complete: results => {
                try {
                    let data = results.data;
                    const previewData = data.slice(0, 5);
                    this.preview.next(previewData);
                    this.fileDataSubject.next(data);
                    this.had_file = true;
                } catch {
                    this.preview.next(null);
                    this.error = 'Failed to parse the file. Please provide a valid csv file.';
                }
            },
            error: error => {
                console.warn('error parsing file\n', error);
            }
        });
    }

    ngOnInit(): void {
        const templateRefresh$ = [];
        templateRefresh$.push(this.templateOptionsSubject);
        templateRefresh$.push(this.fileDataSubject);

        combineLatest(templateRefresh$).pipe(takeUntil(this.onDestroy)).subscribe((r: any) => {
            let options: EventModelOptions = r[0] as EventModelOptions;
            let file_data: string[][] = r[1] as string[][];

            if (!options) {
                options = new EventModelOptions();
            }
            if (options.event_type) {
                this.dataService.changeEventType(options.event_type);
            }

            let headers: string[] = this.getFileHeadersForOptions(file_data, options);
            this.header_associations = this.generateHeaderAssociations(options, headers);

            this.localOptionsSubject.next(options);

            // this.finalOptionsSubject.next(options);
            // }

        });

        const localRefresh$ = [];
        localRefresh$.push(this.localOptionsSubject);
        localRefresh$.push(this.fileDataSubject);

        combineLatest(localRefresh$).pipe(takeUntil(this.onDestroy)).subscribe((r: any) => {
            const options: EventModelOptions = r[0] as EventModelOptions;
            let file_data: string[][] = r[1] as string[][];

            let headers: string[] = this.getFileHeadersForOptions(file_data, options);
            const delta_length = Math.abs(this.header_associations.length - headers.length);
            if (this.header_associations.length < headers.length) {
                // Add empty header associations if new file has more columns
                for (let i = 0; i < delta_length; i++) {
                    this.header_associations.push(this.getHeaderAssociationForHeader(''));
                }
            } else if (this.header_associations.length > headers.length) {
                // Remove extra header associations if new file has fewer columns
                this.header_associations.splice(headers.length, delta_length);
            }

            // Change the source header to match the current options
            headers.forEach((header, i) => {
                const ha = this.header_associations[i];
                ha.source = header;
            });

            this.finalOptionsSubject.next(options);
        });

        this.finalOptionsSubject.pipe(takeUntil(this.onDestroy)).subscribe(options => {
            this.setOptionsHeaderFields(this.header_associations, options);

            const selected_cp_ids = options.property_headers.map(ph => ph[1]).filter(h => !!h);
            if (options.unique_property && !selected_cp_ids.includes(options.unique_property)) {
                options.unique_property = null;
                this.notification.openError('Unique property is not valid for the selected template or file.');
            }

            this.displayOptions = options;
            this.dataService.options$.next(options);
            if (this.isOptionsValid(options)) {
                this.templateService.updateWIPTemplate(options);
            } else {
                this.templateService.updateWIPTemplate(null);
            }
        });
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }

    setOverride(header: HeaderAssociation, change) {
        header.override_id = change;
        const options = this.localOptionsSubject.getValue();
        this.localOptionsSubject.next(options);
    }

    setUniqueProperty(property_id) {
        const options = this.localOptionsSubject.getValue();
        options.unique_property = property_id;
        this.localOptionsSubject.next(options);
    }

    setHeaderTarget(header: HeaderAssociation, $event) {
        header.target_type = $event.value;
        header.override_id = null;
        const options = this.localOptionsSubject.getValue();
        this.localOptionsSubject.next(options);
    }

    setUseColumnIndexes(use_column_indexes: boolean) {
        const options = this.localOptionsSubject.getValue();
        options.use_column_indexes = !!use_column_indexes;
        this.localOptionsSubject.next(options);
    }

    setUpdateOnly(update_only: boolean) {
        const options = this.localOptionsSubject.getValue();
        options.update_only = update_only;
        this.localOptionsSubject.next(options);
    }

    setSkipRows(skip_rows: number) {
        const options = this.localOptionsSubject.getValue();
        options.skip_rows = skip_rows;
        this.localOptionsSubject.next(options);
    }

    setEventType(event_type_id: string) {
        const options = this.localOptionsSubject.getValue();
        options.property_headers.forEach(ph => ph[1] = null);
        options.component_type_headers.forEach(cth => cth[1] = null);
        options.event_type = event_type_id;
        this.localOptionsSubject.next(options);
    }

    setUpsertType(upsert_type: EventModelUpsertType) {
        const options = this.localOptionsSubject.getValue();
        options.upsert_type = upsert_type;
        this.localOptionsSubject.next(options);
    }

    /**
     * Update options if some of the header_associations have changed.
     */
    updateHeaders() {
        const options = this.localOptionsSubject.getValue();
        this.localOptionsSubject.next(options);
    }

    private getFileHeadersForOptions(file_data: string[][], options: EventModelOptions): string[] {
        if (!file_data) return [];
        let headers: string[];
        if (options.skip_rows) {
            file_data = file_data.slice(options.skip_rows);
        }
        if (options.use_column_indexes) {
            // generate the index headers from the max row length
            const max_row = file_data.reduce((max, curr) => max > curr.length ? max : curr.length, 0);
            headers = Array.from(Array(max_row).keys()).map(i => '' + i);
        } else {
            // extract the name headers from the file data
            headers = file_data[0];
        }
        return headers;
    }

    private getHeaderAssociationForHeader(header: string): HeaderAssociation {
        return {
            source: header.trim(),
            target_type: this.HEADER_TYPES.UNUSED,
            override_id: null,
        };
    }

    private generateHeaderAssociations(options: EventModelOptions, headers: string[]): HeaderAssociation[] {
        let unmatched = [];
        // first generate unassociated headers
        const header_associations: HeaderAssociation[] = headers.map(header => {
            return this.getHeaderAssociationForHeader(header);
        });

        if (options.property_headers) {
            options.property_headers.forEach(ph => {
                // ph either [source, id] or [source]
                const header = header_associations.find(h => h.source === ph[0]);
                if (header) {
                    header.target_type = this.HEADER_TYPES.PROPERTY;
                    header.override_id = ph[1] ? ph[1] : null;
                } else {
                    unmatched.push({type: 'constant_property', name: ph[0]});
                }
            });
        }
        if (options.component_type_headers) {
            options.component_type_headers.forEach(ct => {
                const header = header_associations.find(h => h.source === ct[0]);
                if (header) {
                    header.target_type = this.HEADER_TYPES.COMPONENT;
                    header.override_id = ct[1] ? ct[1] : null;
                } else {
                    unmatched.push({type: 'component_type', name: ct[0]});
                }
            });
        }

        if (options.start_date_header) {
            const header = header_associations.find(h => h.source === options.start_date_header);
            if (header) {
                header.target_type = this.HEADER_TYPES.START_DATE;
            } else {
                unmatched.push({type: 'attribute', name: 'Start date'});
            }
        }
        if (options.end_date_header) {
            const header = header_associations.find(h => h.source === options.end_date_header);
            if (header) {
                header.target_type = this.HEADER_TYPES.END_DATE;
            } else {
                unmatched.push({type: 'attribute', name: 'End date'});
            }
        }

        if (this.readonly) {
            this.templateService.reportUnmatchedHeaders(unmatched);
        }
        return header_associations;
    }

    /**
     * Evaluate if the options is valid and emit the null or valid options to optionsChange.
     */
    private isOptionsValid(options: EventModelOptions): boolean {
        // TODO prevent more than one of the single headers

        let has_upsert_type = options.upsert_type != null;
        let has_event_type = options.event_type != null;

        let has_unique_property = !!options.unique_property;

        this.required_configurations = [];

        if ((options.upsert_type === "unique_property" && has_unique_property) &&
            has_event_type) {
            console.log('had valid options', options);
            return true;
        } else {
            if (!has_upsert_type) {
                this.required_configurations.push('Choose the upsert type');
            }
            if (!has_event_type) {
                this.required_configurations.push('Choose the Event Type');
            } else {
                if (options.upsert_type === "unique_property" && !has_unique_property) {
                    this.required_configurations.push('Choose a unique property to upsert against');
                }
            }
            return false;
        }
    }

    /**
     * Generate the model options from this header associations.
     *
     * All and only features that are assigned using the column features selection should call this method.
     */
    private setOptionsHeaderFields(header_associations: HeaderAssociation[], options: EventModelOptions) {
        const all_current_assigned = new Set(header_associations.map(h => h.target_type));

        // extract the start date header
        options.start_date_header = null;
        if (all_current_assigned.has(this.HEADER_TYPES.START_DATE)) {
            options.start_date_header = header_associations
                .find(h => h.target_type === this.HEADER_TYPES.START_DATE).source;
        }
        // extract the end date header
        options.end_date_header = null;
        if (all_current_assigned.has(this.HEADER_TYPES.END_DATE)) {
            options.end_date_header = header_associations
                .find(h => h.target_type === this.HEADER_TYPES.END_DATE).source;
        }

        // extract property headers and optional overrides
        const property_headers = this.header_associations.filter(h => {
            return h.target_type === this.HEADER_TYPES.PROPERTY;
        }).map(h => {
            return [h.source, h.override_id];
        });
        options.property_headers = property_headers;

        const component_headers = this.header_associations.filter(h => {
            return h.target_type === this.HEADER_TYPES.COMPONENT;
        }).map(h => {
            return [h.source, h.override_id];
        });
        options.component_type_headers = component_headers;
    }
}
